import { z } from 'zod'
import {
  AttachmentKind,
  AutoSuggestionState,
  ClassificationSource,
  ClassificationStatus,
  ConversationKind,
  ConversationStatus,
  Folder,
  Intent,
  Language,
  MessageDirection,
  Platform,
  SuggestionStatus,
} from '../enums'
import { PageConnection } from './channels'
import { Id, Instant, Ratio } from './primitives'
import { Reply } from './replies'
import { Suggestion } from './suggestions'

export const QUEUE_DEFAULT_PAGE_LIMIT = 20
export const QUEUE_MAX_PAGE_LIMIT = 100
export const QUEUE_MAX_PAGE_NUMBER = 10_000
export const QUEUE_AUTHOR_FILTER_MAX_CHARS = 200
export const QUEUE_DEFAULT_STATUS: ConversationStatus = 'open'
export const CLOSED_CONVERSATION_MESSAGE = '這個項目已經回覆或已撤回'

export const Classification = z.object({
  status: ClassificationStatus,
  source: ClassificationSource.nullable(),
  intent: Intent.nullable(),
  language: Language.nullable(),
  inDomain: z.boolean().nullable(),
  confidence: Ratio.nullable(),
  rule: z.string().nullable(),
  error: z.string().nullable(),
})
export type Classification = z.infer<typeof Classification>

export const Claim = z.object({
  userId: z.string().min(1),
  displayName: z.string(),
  claimedAt: Instant,
  expiresAt: Instant,
})
export type Claim = z.infer<typeof Claim>

export const ActiveViewer = z.object({
  userId: z.string().min(1),
  displayName: z.string(),
  lastViewedAt: Instant,
})
export type ActiveViewer = z.infer<typeof ActiveViewer>

export const QueueItem = z.object({
  id: Id,
  kind: ConversationKind,
  platform: Platform,
  channelId: Id,
  channelName: z.string(),
  authorName: z.string(),
  authorExternalId: z.string(),
  preview: z.string(),
  messageCount: z.number().int().nonnegative(),
  unreadCount: z.number().int().nonnegative(),
  status: ConversationStatus,
  folder: Folder,
  priorityScore: z.number().nullable(),
  replyDeadlineAt: Instant.nullable(),
  lastMessageAt: Instant,
  createdAt: Instant,
  claim: Claim.nullable(),
  viewedAt: Instant.nullable(),
  activeViewer: ActiveViewer.nullable(),
  classification: Classification,
  suggestionStatus: SuggestionStatus.nullable(),
  pageConnection: PageConnection,
})
export type QueueItem = z.infer<typeof QueueItem>

export const ThreadMessage = z.object({
  id: Id,
  direction: MessageDirection,
  authorName: z.string(),
  text: z.string().nullable(),
  sentAt: Instant,
  editedAt: Instant.nullable(),
  removedAt: Instant.nullable(),
  redactedAt: Instant.nullable(),
  sentOutside: z.boolean().default(false),
  attachmentKinds: z.array(AttachmentKind).optional(),
})
export type ThreadMessage = z.infer<typeof ThreadMessage>

export const POST_STATUSES = ['pending', 'ready', 'unavailable'] as const
export const PostStatus = z.enum(POST_STATUSES)
export type PostStatus = z.infer<typeof PostStatus>

export const PostContext = z.object({
  externalId: z.string().min(1),
  status: PostStatus,
  text: z.string().nullable(),
  permalinkUrl: z.url({ protocol: /^https$/ }).nullable(),
  postedAt: Instant.nullable(),
})
export type PostContext = z.infer<typeof PostContext>

export const QueueItemDetail = QueueItem.extend({
  messages: z.array(ThreadMessage),
  olderMessageCount: z.number().int().nonnegative().default(0),
  suggestion: Suggestion.nullable(),
  autoSuggestion: AutoSuggestionState.catch('manual'),
  replies: z.array(Reply),
  replyRequiresClaim: z.boolean(),
  latestInboundMessageId: Id.nullable(),
  post: PostContext.nullable().default(null),
})
export type QueueItemDetail = z.infer<typeof QueueItemDetail>

export const QueueListQuery = z.strictObject({
  folder: Folder.default('inbox'),
  status: ConversationStatus.default(QUEUE_DEFAULT_STATUS),
  intent: Intent.optional(),
  kind: ConversationKind.optional(),
  source: ClassificationSource.optional(),
  author: z.string().min(1).max(QUEUE_AUTHOR_FILTER_MAX_CHARS).optional(),
  page: z.coerce.number().int().min(1).max(QUEUE_MAX_PAGE_NUMBER).default(1),
  limit: z.coerce.number().int().min(1).max(QUEUE_MAX_PAGE_LIMIT).default(QUEUE_DEFAULT_PAGE_LIMIT),
})
export type QueueListQuery = z.infer<typeof QueueListQuery>

export const QueueUnreadSummary = z.object({
  conversations: z.number().int().nonnegative(),
})
export type QueueUnreadSummary = z.infer<typeof QueueUnreadSummary>

export const ClaimRequest = z.strictObject({ takeover: z.boolean().default(false) })
export type ClaimRequest = z.infer<typeof ClaimRequest>

export const ClassificationPatchRequest = z
  .strictObject({ intent: Intent.optional(), inDomain: z.boolean().optional(), folder: Folder.optional() })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'at least one field is required' })
export type ClassificationPatchRequest = z.infer<typeof ClassificationPatchRequest>

export const ViewHeartbeatRequest = z.strictObject({ active: z.boolean().default(false) })
export type ViewHeartbeatRequest = z.infer<typeof ViewHeartbeatRequest>
